import type { PeriodDateRange } from '@/constants/ObjectivePeriodLens';

import { FROM_PARAM, PERIOD_PARAM, TO_PARAM } from '@/constants/ObjectivePeriodLens';
import { ObjectiveType } from '@/constants/ObjectiveType';
import { RouteName } from '@/navigation/types';

/**
 * Path builders for the Objectives module. Centralizing the `?period=` query-string
 * handling here keeps every link (cards, breadcrumbs, redirects) consistent.
 */

interface WithPeriodParams {
    path: string;
    period?: string;
    /**
     * The bounds of a custom range, carried beside it: `custom-dates` names no cycle, so a link
     * that dropped them would land the next screen on a lens it cannot resolve, and it would fall
     * back to the default cycle without saying so.
     */
    range?: PeriodDateRange;
}

const withPeriod = ({ path, period, range }: WithPeriodParams): string => {
    if (!period) {
        return path;
    }

    const params = new URLSearchParams({ [PERIOD_PARAM]: period });
    if (range?.from && range.to) {
        params.set(FROM_PARAM, range.from);
        params.set(TO_PARAM, range.to);
    }

    return `${path}?${params.toString()}`;
};

interface PeriodLinkParams {
    period?: string;
    range?: PeriodDateRange;
}

export const objectivesPath = ({ period, range }: PeriodLinkParams = {}): string =>
    withPeriod({ path: RouteName.Objectives, period, range });

/**
 * The two screens that read the objectives through one lens -- the period, the list's filters and
 * the pillar scope -- each lead to the other with it. `lensSearch` is the query string of the screen
 * left, as `location.search` gives it, `?` included: the lens is carried whole, never rebuilt.
 */
interface PillarPathParams {
    pillarId: string;
    lensSearch: string;
}

export const pillarPath = ({ pillarId, lensSearch }: PillarPathParams): string =>
    `${RouteName.ObjectivePillar.replace(':pillarId', pillarId)}${lensSearch}`;

/** The objectives page reading the lens a pillar's page holds; see `pillarPath`. */
export const objectivesLensPath = (lensSearch: string): string =>
    `${RouteName.Objectives}${lensSearch}`;

export const objectiveDetailPath = (id: string): string =>
    RouteName.ObjectiveDetail.replace(':id', id);

export const objectiveEditPath = (id: string): string => RouteName.ObjectiveEdit.replace(':id', id);

/**
 * The location state of the module's screens that have a Retour. Every navigation inside the module
 * toward one of them says the screen it leaves is the previous history entry, so that Retour steps
 * back to it, with its period, its filters and its scroll, instead of opening a screen it guesses.
 * The steps inside a flow replace one another (type choice, form, the published objective), so the
 * screen the flow started from stays exactly one step back. Arriving without it (a link opened in a
 * new tab, an email), a Retour opens its fallback screen instead.
 */
export interface ObjectivesLocationState {
    canStepBack?: boolean;
    /** The form was opened from the type choice, which it replaced: its Retour reopens that choice. */
    fromTypeChoice?: boolean;
    /** The creation began in the reader's own portal: the new objective starts as theirs. */
    forReader?: boolean;
}

/**
 * The one way to build that state: by default, "the screen I leave is one step back". A step that
 * takes the place of another passes on what it was itself given.
 */
export const stepBackState = ({
    canStepBack = true,
    fromTypeChoice,
    forReader,
}: ObjectivesLocationState = {}): ObjectivesLocationState => ({
    canStepBack,
    fromTypeChoice,
    forReader,
});

type NewObjectiveParams = Pick<PeriodLinkParams, 'period'>;

/**
 * The creation begun from a screen filtered on a period carries that period to the type choice and
 * on to the form, which opens on it when it may hold an objective; any other lens keeps its default.
 */
export const newObjectivePath = ({ period }: NewObjectiveParams = {}): string =>
    withPeriod({ path: RouteName.ObjectivesNew, period });

interface NewObjectiveOfTypeParams extends NewObjectiveParams {
    type: string;
}

export const newObjectiveOfTypePath = ({ type, period }: NewObjectiveOfTypeParams): string =>
    withPeriod({ path: RouteName.ObjectivesNewType.replace(':type', type), period });

export const employeesWithoutObjectivesPath = ({ period, range }: PeriodLinkParams = {}): string =>
    withPeriod({ path: RouteName.ObjectivesWithoutObjective, period, range });

interface NewObjectiveForEmployeeParams {
    employeeId: string;
    period?: string;
}

/**
 * The objective form opened for one employee. The `:type` segment carries the objective type in
 * lower case, the same spelling `CreateObjectiveTypePage` builds its own links with; the form
 * reads `?employeeId=` to start with that employee already picked.
 */
export const newObjectiveForEmployeePath = ({
    employeeId,
    period,
}: NewObjectiveForEmployeeParams): string => {
    const path = RouteName.ObjectivesNewType.replace(
        ':type',
        ObjectiveType.INDIVIDUAL.toLowerCase(),
    );
    const params = new URLSearchParams({ employeeId });
    if (period) {
        params.set('period', period);
    }

    return `${path}?${params.toString()}`;
};

/**
 * Reads the raw `?employeeId=` value from a `location.search` string, or `undefined` if absent --
 * the same role `readPeriodParam` plays for the cycle.
 */
export const readEmployeeIdParam = (search: string): string | undefined =>
    new URLSearchParams(search).get('employeeId') ?? undefined;
